// src/cockpit4/components/RuntimeGovernancePanel.tsx
// ============================================
// Runtime 收敛治理看板
//
// 背景：XEAOS 曾把同一套"企业大脑/内容流水线"拆成 52 个 Runtime 实例，
//       L4 内容层 17 个、L5 决策层 10 个彼此重复（同为 Scheduler 触发 + 写 Daily + 调 LLM）。
// 本面板把 52 实例映射到目标 6 层（L0/L3/L4/L5/L6），标注每个 Runtime 的
//       收敛动作：保留(root/keep) / 并入(merge→Content|Business) / 移出(move→products) / 归并(fold)。
// 动态层：叠加 useRuntimeStore 的实时健康度（无数据时显示占位点）。
// 说明：治理目标是架构规划（配置），实时健康度是运行态（数据），二者分层展示。
// ============================================

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronRight,
  Boxes,
  GitMerge,
  ArrowDownToLine,
  FolderTree,
} from "lucide-react";
import { useRuntimeStore } from "../../stores/runtimeStore";

// ============================================
// 治理目标元信息（静态架构配置）
// ============================================

type Action = "root" | "keep" | "merge" | "move" | "fold";

interface GItem {
  /** RuntimeName 枚举 value（与 runtimeStore.runtimes[].name 匹配） */
  v: string;
  /** 可读名称 */
  label: string;
  /** 收敛动作 */
  action: Action;
  /** 合并/移出目标（merge/move 时有值） */
  target?: string;
  /** 是否默认关闭（暂不开放） */
  disabled?: boolean;
  /** 资源是否复用他处（fold 目标） */
  into?: string;
}

interface GLayer {
  id: string;
  badge: string;
  title: string;
  desc: string;
  actionText: string;
  from: string;
  to: string;
  accent: string;
  items: GItem[];
}

const LAYERS: GLayer[] = [
  {
    id: "l0",
    badge: "L0",
    title: "平台基础设施",
    desc: "职责稳定 · 穷尽复用",
    actionText: "全部保留",
    from: "14",
    to: "14",
    accent: "#A78BFA",
    items: [
      { v: "hermes", label: "Hermes · COO", action: "root" },
      { v: "knowledge", label: "Knowledge · 大脑", action: "root" },
      { v: "agent", label: "Agent · 执行", action: "root" },
      { v: "workflow", label: "Workflow · 流程", action: "root" },
      { v: "memory", label: "Memory · 记忆", action: "root" },
      { v: "enterprise", label: "Enterprise · 上下文", action: "root" },
      { v: "integration", label: "Integration · 集成", action: "root" },
      { v: "notification", label: "Notification", action: "root" },
      { v: "scheduler", label: "Scheduler · 调度", action: "root" },
      { v: "prompt", label: "Prompt · 提示词", action: "root" },
      { v: "tool", label: "Tool · 工具", action: "root" },
      { v: "llm", label: "LLM · 多模型", action: "root" },
      { v: "skill", label: "Skill · 技能市场", action: "root" },
      { v: "workspace", label: "Workspace · 工作区", action: "root" },
    ],
  },
  {
    id: "l3",
    badge: "L3",
    title: "数据标准层",
    desc: "数据基准 · 只被查不主动跑",
    actionText: "保留 · 归并 1",
    from: "6",
    to: "5",
    accent: "#34D399",
    items: [
      { v: "crm", label: "CRM", action: "keep" },
      { v: "sku", label: "SKU", action: "keep" },
      { v: "sop", label: "SOP", action: "keep" },
      { v: "brand_library", label: "Brand Library", action: "keep" },
      { v: "revenue_engine", label: "Revenue Engine", action: "keep" },
      { v: "knowledge_graph", label: "Knowledge Graph", action: "fold", into: "Knowledge 域" },
    ],
  },
  {
    id: "l4",
    badge: "L4",
    title: "内容引擎",
    desc: "一条流水线：选题→策略→生产→审核→发布→复盘",
    actionText: "合并重灾区",
    from: "17",
    to: "1",
    accent: "#F59E0B",
    items: [
      { v: "content", label: "Content · 母体", action: "root" },
      { v: "content_factory", label: "Content Factory", action: "merge", target: "Content" },
      { v: "content_calendar", label: "Content Calendar", action: "merge", target: "Content" },
      { v: "content_lifecycle", label: "Content Lifecycle", action: "merge", target: "Content" },
      { v: "content_strategy", label: "Content Strategy", action: "merge", target: "Content" },
      { v: "calendar", label: "Calendar", action: "merge", target: "Content" },
      { v: "topic_engine", label: "Topic Engine", action: "merge", target: "Content" },
      { v: "news", label: "News", action: "merge", target: "Content" },
      { v: "editorial", label: "Editorial", action: "merge", target: "Content" },
      { v: "video_pipeline", label: "Video Pipeline", action: "merge", target: "Content" },
      { v: "media_studio", label: "Media Studio", action: "merge", target: "Content" },
      { v: "geo", label: "GEO", action: "merge", target: "Content" },
      { v: "dso", label: "DSO", action: "merge", target: "Content" },
      { v: "growth_factory", label: "Growth Factory", action: "merge", target: "Content" },
      { v: "coze_workflow_gen", label: "Coze Workflow Gen", action: "merge", target: "Content" },
      { v: "coze_executor", label: "Coze Executor", action: "merge", target: "Content" },
      { v: "experience", label: "Experience", action: "merge", target: "Content", disabled: true },
    ],
  },
  {
    id: "l5",
    badge: "L5",
    title: "企业大脑",
    desc: "巡检→目标→决策→CEO 简报，统一 Daily 出口",
    actionText: "合并重灾区",
    from: "10",
    to: "1",
    accent: "#F87171",
    items: [
      { v: "business", label: "Business · 母体", action: "root" },
      { v: "business_engine", label: "Business Engine", action: "merge", target: "Business" },
      { v: "business_operator", label: "Business Operator", action: "merge", target: "Business" },
      { v: "enterprise_brain", label: "Enterprise Brain", action: "merge", target: "Business" },
      { v: "growth_engine", label: "Growth Engine", action: "merge", target: "Business" },
      { v: "autopilot", label: "Autopilot", action: "merge", target: "Business" },
      { v: "ceo", label: "AI CEO", action: "merge", target: "Business" },
      { v: "sales", label: "AI Sales", action: "merge", target: "Business" },
      { v: "knowledge_evolution", label: "Knowledge Evolution", action: "merge", target: "Business" },
      { v: "geodiagnostic", label: "GEO Diagnostic", action: "merge", target: "Business" },
    ],
  },
  {
    id: "l6",
    badge: "L6",
    title: "产品能力",
    desc: "业务线 ≠ 系统能力，移出内核按需加载",
    actionText: "全部移出",
    from: "6",
    to: "6",
    accent: "#38BDF8",
    items: [
      { v: "hotel", label: "Hotel", action: "move", target: "products" },
      { v: "gold_factory", label: "Gold Factory", action: "move", target: "products" },
      { v: "ai_destiny", label: "AI Destiny", action: "move", target: "products" },
      { v: "marketing_os", label: "Marketing OS", action: "move", target: "products" },
      { v: "quant", label: "Quant · 量化", action: "move", target: "products" },
      { v: "project", label: "Project", action: "move", target: "products" },
    ],
  },
  {
    id: "lx",
    badge: "待归并",
    title: "默认关闭 · 未来接入",
    desc: "暂不开放，视为方向预留",
    actionText: "并入",
    from: "1",
    to: "1",
    accent: "#9CA3AF",
    items: [{ v: "agent_mesh", label: "Agent Mesh", action: "fold", into: "Agent 域", disabled: true }],
  },
];

// ============================================
// 动作徽章样式（纯函数）
// ============================================

function actionStyle(action: Action) {
  switch (action) {
    case "root":
      return { border: "#4F46E5", bg: "rgba(99,102,241,0.12)", text: "#A5B4FC", label: "母体" };
    case "keep":
      return { border: "#10B981", bg: "rgba(16,185,129,0.10)", text: "#34D399", label: "保留" };
    case "merge":
      return { border: "#F59E0B", bg: "rgba(245,158,11,0.10)", text: "#FBBF24", label: "并入" };
    case "move":
      return { border: "#38BDF8", bg: "rgba(56,189,248,0.10)", text: "#7DD3FC", label: "移出" };
    case "fold":
      return { border: "#9CA3AF", bg: "rgba(156,163,175,0.10)", text: "#D1D5DB", label: "归并" };
    default:
      return { border: "#9CA3AF", bg: "rgba(156,163,175,0.10)", text: "#D1D5DB", label: "归并" };
  }
}

function healthColor(health?: string) {
  switch (health) {
    case "healthy":
      return "#10B981";
    case "degraded":
      return "#F59E0B";
    case "unhealthy":
      return "#F87171";
    default:
      return "#52525B";
  }
}

// ============================================
// 主组件
// ============================================

export default function RuntimeGovernancePanel() {
  const runtimes = useRuntimeStore((s) => s.runtimes);
  const healthSummary = useRuntimeStore((s) => s.healthSummary);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    l0: true, l3: true, l4: true, l5: true, l6: false, lx: false,
  });

  // 实时健康度：name(value) → health
  const healthMap = new Map<string, string>();
  runtimes.forEach((r) => healthMap.set(r.name as string, r.health));

  // 统计：常驻目标层（L0+L3+L4+L5 母体）合计
  const targetCore = LAYERS.filter((l) => ["l0", "l3", "l4", "l5"].includes(l.id))
    .reduce((acc, l) => acc + (l.id === "l3" ? 5 : l.from === l.to ? Number(l.to) : 1), 0);

  const toggle = (id: string) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  return (
    <div className="space-y-2">
      {/* 顶部摘要条 */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 text-[10px] font-mono text-purple-300 bg-purple-500/10 border border-purple-500/20 px-2 py-0.5 rounded">
          <Boxes size={10} /> 现状 52 实例
        </span>
        <span className="text-[10px] font-mono text-gray-600">→</span>
        <span className="inline-flex items-center gap-1 text-[10px] font-mono text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded">
          <GitMerge size={10} /> 目标 {targetCore} 常驻内核 + 6 产品懒加载
        </span>
        <span className="ml-auto text-[9px] font-mono text-gray-600">
          {healthSummary.readyCount || "—"}/{(healthSummary.runtimeCount || 0) || "—"} ready
        </span>
      </div>

      {/* 层次分组 */}
      {LAYERS.map((layer) => {
        const isOpen = !!expanded[layer.id];
        const mergeCount = layer.items.filter((it) => it.action === "merge").length;
        const moveCount = layer.items.filter((it) => it.action === "move").length;
        return (
          <div
            key={layer.id}
            className="rounded-lg border border-white/[0.05] bg-white/[0.015] overflow-hidden"
          >
            {/* 层头 */}
            <button
              onClick={() => toggle(layer.id)}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/[0.02] transition-colors text-left"
            >
              <ChevronRight
                size={13}
                className={`text-gray-500 transition-transform ${isOpen ? "rotate-90" : ""}`}
              />
              <span
                className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border"
                style={{ color: layer.accent, borderColor: layer.accent + "44", background: layer.accent + "11" }}
              >
                {layer.badge}
              </span>
              <span className="text-[12px] font-medium text-white">{layer.title}</span>
              <span className="text-[9px] font-mono text-gray-500 hidden sm:inline">{layer.desc}</span>
              <span className="ml-auto flex items-center gap-1 text-[10px] font-mono">
                <span className="text-gray-400">{layer.from}</span>
                <span className="text-gray-600">→</span>
                <span className={mergeCount || moveCount ? "text-amber-300" : "text-emerald-300"}>{layer.to}</span>
                <span className={`ml-1 px-1.5 py-0.5 rounded text-[9px] ${
                  mergeCount || moveCount ? "text-amber-300 bg-amber-500/10" : "text-emerald-300 bg-emerald-500/10"
                }`}>
                  {layer.actionText}
                </span>
              </span>
            </button>

            {/* 展开内容 */}
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  key="body"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                  className="overflow-hidden"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5 px-3 pb-3">
                    {layer.items.map((it) => {
                      const st = actionStyle(it.action);
                      const hc = healthColor(healthMap.get(it.v));
                      return (
                        <div
                          key={it.v}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded-md border bg-white/[0.01] ${
                            it.disabled ? "opacity-45 border-white/[0.03]" : "border-white/[0.06]"
                          }`}
                          title={`${it.label} — ${it.action === "merge" ? "并入" + it.target : it.action === "move" ? "移出→" + it.target : it.into ? "归并→" + it.into : "保留"}`}
                        >
                          {/* 实时健康点 */}
                          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: hc, boxShadow: `0 0 6px ${hc}` }} />
                          <span className="text-[11px] font-mono text-gray-200 truncate flex-1">{it.label}</span>
                          {it.into && (
                            <span className="text-[8px] font-mono text-gray-500" title={`归并→${it.into}`}>
                              <FolderTree size={9} />
                            </span>
                          )}
                          <span
                            className="flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-mono flex-shrink-0"
                            style={{ color: st.text, background: st.bg, border: `1px solid ${st.border}` }}
                          >
                            {it.action === "merge" ? <GitMerge size={8} /> : it.action === "move" ? <ArrowDownToLine size={8} /> : null}
                            {st.label}
                            {it.target && <span className="opacity-80">·{it.target}</span>}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}